import { Box, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import styled from 'styled-components';
import ErrorMessage from '@shared/components/ErrorMessage';

interface User {
  firstName?: string;
  lastName?: string;
  username?: string;
}

interface AskQuestionModalProps {
  mode?: 'ask' | 'answer';
  reviewId: number;
  companyName: string;
  reviewAuthor?: User;
  questionText?: string; // For answer mode - the question being answered
  onSubmit: (text: string, showValidation: (show: boolean) => void) => void;
  onRequestCall?: () => void;
  onTextChange?: (
    text: string,
    isValid: boolean,
    setShowValidation: (show: boolean) => void,
  ) => void;
}

const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0;
`;

const IntroSection = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 24px;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.04) 0%,
    rgba(120, 187, 241, 0.02) 100%
  );
  border-radius: 16px;
  border: 2px solid rgba(11, 116, 218, 0.12);
`;

const IconCircle = styled(Box)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0b74da 0%, #78bbf1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 4px 16px rgba(11, 116, 218, 0.25);
  flex-shrink: 0;
`;

const StyledTextField = styled(TextField)<{ $mode?: 'ask' | 'answer' }>`
  & .MuiOutlinedInput-root {
    background: white;
    border-radius: 12px;
    transition: all 0.3s ease;
    padding: 16px;

    &:hover {
      background: white;
    }

    &.Mui-focused {
      background: white;
      box-shadow: 0 0 0 3px rgba(11, 116, 218, 0.1);
    }
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: rgba(11, 116, 218, 0.3);
    border-width: 2px;
  }

  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(11, 116, 218, 0.6);
  }

  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #0b74da;
    border-width: 2px;
  }

  & textarea {
    padding: 0 !important;
    line-height: 1.75;
    font-size: 15px;
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Plus Jakarta Sans', 'Oxygen',
      'Ubuntu', 'Cantarell', sans-serif;
    color: rgba(0, 0, 0, 0.87);

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
      opacity: 1;
      line-height: 1.75;
    }
  }

  & .MuiInputBase-input {
    padding: 0;
  }
`;

const QuestionDisplaySection = styled(Box)`
  padding: 20px 24px 24px 24px;
  background: linear-gradient(
    135deg,
    rgba(11, 116, 218, 0.03) 0%,
    rgba(120, 187, 241, 0.01) 100%
  );
  border-radius: 16px;
  border: 2px solid rgba(11, 116, 218, 0.15);
  position: relative;
  margin-top: 16px;
`;

const QuestionIconWrapper = styled(Box)`
  position: absolute;
  top: -12px;
  left: 20px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0b74da 0%, #78bbf1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 4px 12px rgba(11, 116, 218, 0.25);
`;

const QuestionLabel = styled(Typography)`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(11, 116, 218, 0.7);
  margin-bottom: 10px;
`;

const QuestionText = styled(Typography)`
  font-size: 15px;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
`;

const CharacterCount = styled(Typography)`
  text-align: right;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 8px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

const TipBox = styled(Box)<{ $mode?: 'ask' | 'answer' }>`
  padding: 18px 20px;
  background: ${props =>
    props.$mode === 'answer'
      ? 'rgba(76, 175, 80, 0.06)'
      : 'rgba(11, 116, 218, 0.06)'};
  border-radius: 12px;
  border: 1px solid
    ${props =>
      props.$mode === 'answer'
        ? 'rgba(76, 175, 80, 0.2)'
        : 'rgba(11, 116, 218, 0.2)'};
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const TipIcon = styled(Box)<{ $mode?: 'ask' | 'answer' }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => (props.$mode === 'answer' ? '#4CAF50' : '#0b74da')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
  margin-top: 1px;
`;

const AskQuestionModal: React.FC<AskQuestionModalProps> = ({
  mode = 'ask',
  reviewId: _reviewId,
  companyName: _companyName,
  reviewAuthor,
  questionText,
  onSubmit,
  onRequestCall,
  onTextChange,
}) => {
  const [text, setText] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const maxLength = mode === 'answer' ? 1000 : 500;
  const minLength = 25;

  const authorName = reviewAuthor?.username || 'the reviewer';

  const isValid = text.trim().length >= minLength;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (isValid) {
        onSubmit(text.trim(), setShowValidation);
      }
    }
  };

  return (
    <ContentWrapper>
      {mode === 'ask'
        ? (
            <IntroSection>
              <IconCircle>💬</IconCircle>
              <Box sx={{ flex: 1, pt: 0.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.75)',
                    lineHeight: 1.75,
                    fontSize: '15px',
                  }}
                >
                  You are asking
                  {' '}
                  <strong>{authorName}</strong>
                  {' '}
                  a question about this
                  deal review. You can also
                  {' '}
                  <Box
                    component="span"
                    sx={{
                      'color': '#0b74da',
                      'fontWeight': 600,
                      'textDecoration': 'underline',
                      'cursor': 'pointer',
                      'transition': 'color 0.2s ease',
                      'textDecorationThickness': '1.5px',
                      'textUnderlineOffset': '2px',
                      '&:hover': {
                        color: '#78bbf1',
                      },
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRequestCall?.();
                    }}
                  >
                    request a call
                  </Box>
                  {' '}
                  if you prefer.
                </Typography>
              </Box>
            </IntroSection>
          )
        : (
            <QuestionDisplaySection>
              <QuestionIconWrapper>❓</QuestionIconWrapper>
              <QuestionLabel>Question</QuestionLabel>
              <QuestionText>{questionText}</QuestionText>
            </QuestionDisplaySection>
          )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0, 0, 0, 0.8)',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            {mode === 'answer' ? 'Your Answer' : 'Your Question'}
          </Typography>
        </Box>
        <StyledTextField
          data-testid={mode === 'answer' ? 'answer-input' : 'question-input'}
          $mode={mode}
          fullWidth
          multiline
          rows={mode === 'answer' ? 6 : 5}
          placeholder={
            mode === 'answer'
              ? 'Share your insights, experiences, and any relevant details that would be helpful to answer this question thoroughly.'
              : 'What would you like to know about this deal? Be specific to get the most helpful response.'
          }
          value={text}
          onChange={(e) => {
            if (e.target.value.length <= maxLength) {
              const newText = e.target.value;
              setText(newText);
              const valid = newText.trim().length >= minLength;
              onTextChange?.(newText, valid, setShowValidation);
            }
          }}
          onKeyDown={handleKeyPress}
          autoFocus
          variant="outlined"
        />
        {showValidation && !isValid && (
          <Box sx={{ mt: 1.5 }}>
            <ErrorMessage
              error={`${mode === 'answer' ? 'Answer' : 'Question'} must be at least ${minLength} characters`}
            />
          </Box>
        )}
        <CharacterCount>
          {text.length}
          {' '}
          /
          {maxLength}
        </CharacterCount>
      </Box>

      <TipBox $mode={mode}>
        <TipIcon $mode={mode}>💡</TipIcon>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(0, 0, 0, 0.75)',
            fontSize: '0.875rem',
            lineHeight: 1.7,
            flex: 1,
          }}
        >
          <strong>Tip:</strong>
          {' '}
          {mode === 'answer'
            ? 'Provide a comprehensive answer. The person who asked will be notified when you respond.'
            : 'Be specific in your question. The deal team will be notified and can respond with detailed insights about their experience.'}
        </Typography>
      </TipBox>
    </ContentWrapper>
  );
};

export default AskQuestionModal;
export type { AskQuestionModalProps };

// Legacy export name for compatibility
export { AskQuestionModal as QuestionAnswerModal };
